/**
 * Composer / message chips for attached Nostr context (packs, @mentions, refs).
 */

import { useState } from 'react';
import {
  X,
  User,
  FileText,
  MessagesSquare,
  Sparkles,
  BookOpen,
  Radio,
  BadgeCheck,
} from 'lucide-react';

import type { ChatMention, NostrContextChip } from '@/lib/workspace/types';
import { chipFromMention } from '@/lib/nostr/packs';
import { cn } from '@/lib/utils';

function packIcon(pack: NostrContextChip['pack']) {
  switch (pack) {
    case 'my-profile':
    case 'profile-brief':
    case 'profile-recent':
      return <User className="w-3.5 h-3.5 shrink-0" />;
    case 'thread':
      return <MessagesSquare className="w-3.5 h-3.5 shrink-0" />;
    case 'my-recent':
      return <Sparkles className="w-3.5 h-3.5 shrink-0" />;
    case 'article':
      return <BookOpen className="w-3.5 h-3.5 shrink-0" />;
    case 'network-search':
      return <Radio className="w-3.5 h-3.5 shrink-0" />;
    default:
      return <FileText className="w-3.5 h-3.5 shrink-0" />;
  }
}

function packHint(pack: NostrContextChip['pack']): string {
  switch (pack) {
    case 'my-profile':
      return 'profile';
    case 'my-recent':
      return 'profile + notes';
    case 'profile-brief':
      return 'profile';
    case 'profile-recent':
      return 'profile + notes';
    case 'thread':
      return 'thread';
    case 'article':
      return 'article';
    case 'network-search':
      return 'follows search';
    default:
      return 'event';
  }
}

function isProfilePack(pack: NostrContextChip['pack']): boolean {
  return (
    pack === 'my-profile' ||
    pack === 'my-recent' ||
    pack === 'profile-brief' ||
    pack === 'profile-recent'
  );
}

function ChipThumb({ c }: { c: NostrContextChip }) {
  const [imgFailed, setImgFailed] = useState(false);
  const cover =
    c.image &&
    (c.pack === 'article' || c.pack === 'single' || c.pack === 'thread')
      ? c.image
      : null;
  const avatar = c.picture && !imgFailed ? c.picture : null;

  // Article / event cover (prefer rectangular banner)
  if (cover && !imgFailed) {
    return (
      <img
        src={cover}
        alt=""
        className="w-10 h-7 rounded-md object-cover shrink-0 bg-[#1a1a1a]"
        onError={() => setImgFailed(true)}
      />
    );
  }
  // Profile avatar
  if (avatar) {
    return (
      <img
        src={avatar}
        alt=""
        className="w-7 h-7 rounded-full object-cover shrink-0 bg-[#1a1a1a]"
        onError={() => setImgFailed(true)}
      />
    );
  }
  return (
    <div
      className={cn(
        'shrink-0 bg-[#1a1a1a] flex items-center justify-center text-[#5a5a5a]',
        isProfilePack(c.pack) ? 'w-7 h-7 rounded-full' : 'w-7 h-7 rounded-md',
      )}
    >
      {packIcon(c.pack)}
    </div>
  );
}

export function NostrContextChips({
  chips,
  onRemove,
  className,
  readOnly = false,
}: {
  chips: NostrContextChip[];
  onRemove?: (id: string) => void;
  className?: string;
  readOnly?: boolean;
}) {
  if (!chips.length) return null;

  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {chips.map((c) => (
        <div
          key={c.id}
          className={cn(
            'group/chip relative flex items-center gap-2 rounded-xl border border-[#2a2a2a] bg-[#0f0f0f] pl-2 py-1.5 max-w-[240px]',
            readOnly || !onRemove ? 'pr-2.5' : 'pr-1',
          )}
          title={`${c.title}\n${c.ref}\n${packHint(c.pack)}`}
        >
          <ChipThumb c={c} />
          <div className="min-w-0 flex-1">
            <div className="text-[12px] text-[#e8e6e3] truncate leading-tight">
              {c.title}
            </div>
            <div className="text-[10px] text-[#5a5a5a] truncate flex items-center gap-1">
              {c.nip05 ? (
                <>
                  <span className="truncate">{c.nip05}</span>
                  {c.nip05Verified === true && (
                    <BadgeCheck
                      className="w-3 h-3 shrink-0 text-sky-400/80"
                      aria-label="NIP-05 verified"
                    />
                  )}
                  {c.nip05Verified === false && (
                    <span className="shrink-0 text-amber-500/70">unverified</span>
                  )}
                </>
              ) : (
                packHint(c.pack)
              )}
            </div>
          </div>
          {!readOnly && onRemove && (
            <button
              type="button"
              onClick={() => onRemove(c.id)}
              className="p-1 rounded-md text-[#3a3a3a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a] transition-colors"
              title="Remove"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/** Render mention list as the same visual chips (message bubble / composer). */
export function MentionChips({
  mentions,
  onRemove,
  className,
  readOnly = false,
}: {
  mentions: ChatMention[];
  onRemove?: (id: string) => void;
  className?: string;
  readOnly?: boolean;
}) {
  if (!mentions.length) return null;
  const chips = mentions.map((m) => ({
    ...chipFromMention(m),
    id: m.id,
    fromMention: true as const,
  }));
  return (
    <NostrContextChips
      chips={chips}
      onRemove={onRemove}
      className={className}
      readOnly={readOnly}
    />
  );
}
